import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Grid, Paper, Typography, Card, CardContent, Stack,
  Chip, Button, Select, MenuItem, FormControl, Alert, Divider, Container,
  Modal, IconButton, TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  LocalCarWash as WashIcon,
  Speed as SpeedIcon,
  EventAvailable as AvailableIcon,
  EventBusy as BusyIcon,
  Assessment as ChartIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  DeleteForever as DeleteForeverIcon,
  Build as BuildIcon,
  FilterList as FilterListIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import Chart from 'chart.js/auto';

const API_URL = process.env.REACT_APP_API_URL;

export default function Frotas() {
  const [veiculos, setVeiculos] = useState([]);
  const [alugueis, setAlugueis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [modalAberto, setModalAberto] = useState(false);
  const [modalTitulo, setModalTitulo] = useState('');
  const [modalDados, setModalDados] = useState([]);
  const [buscaModal, setBuscaModal] = useState('');
  const [modalAlugueisAberto, setModalAlugueisAberto] = useState(false);
  const [aluguelsSelecionados, setAluguelsSelecionados] = useState([]);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);
  const [loadingAlugueis, setLoadingAlugueis] = useState(false);
  const [modalExcluidosAberto, setModalExcluidosAberto] = useState(false);
  const [buscaExcluidos, setBuscaExcluidos] = useState('');
  const [manutencoes, setManutencoes] = useState([]);
  const [duracaoSelecionada, setDuracaoSelecionada] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [resVeiculos, resAlugueis, resManutencoes] = await Promise.all([
        axios.get(`${API_URL}/frota`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/alugueis`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/manutencoes`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setVeiculos(resVeiculos.data);
      setAlugueis(resAlugueis.data);
      setManutencoes(resManutencoes.data);
    } catch (err) {
      console.error('Erro ao carregar dados da frota:', err);
      mostrarSnackbar('Erro ao carregar dados da frota', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 📅 Buscar aluguéis de um veículo específico
  const abrirAluguelsPorVeiculo = async (veiculo) => {
    setVeiculoSelecionado(veiculo);
    setLoadingAlugueis(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/alugueis/veiculo/${veiculo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAluguelsSelecionados(response.data.alugueis || []);
      setModalAlugueisAberto(true);
    } catch (err) {
      console.error('Erro ao carregar aluguéis:', err);
      mostrarSnackbar('Erro ao carregar aluguéis do veículo', 'error');
    } finally {
      setLoadingAlugueis(false);
    }
  };

  const mostrarSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar({ ...snackbar, open: false }), 4000);
  };

  // Função para abrir modal com filtro
  const abrirModal = (titulo, filtro) => {
    let dadosFiltrados = [];

    switch (filtro) {
      case 'total':
        dadosFiltrados = veiculosAtivos;
        break;
      case 'disponiveis':
        const veiculosComAluguelAtivo = new Set(
          alugueis
            .filter(a => ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel))
            .map(a => a.id_veiculo)
        );
        dadosFiltrados = veiculosAtivos.filter(v =>
          !veiculosComAluguelAtivo.has(v.id) && !veiculosEmManutencaoSet.has(v.id)
        );
        break;
      case 'emManutencao':
        dadosFiltrados = veiculosAtivos.filter(v => veiculosEmManutencaoSet.has(v.id));
        break;
      case 'reservados':
        const idsReservados = alugueis
          .filter(a => a.status_aluguel === 'Reservado')
          .map(a => a.id_veiculo);
        dadosFiltrados = veiculosAtivos.filter(v => idsReservados.includes(v.id));
        break;
      case 'emAndamento':
        const idsEmAndamento = alugueis
          .filter(a => ['Em Andamento', 'Ativo'].includes(a.status_aluguel))
          .map(a => a.id_veiculo);
        dadosFiltrados = veiculosAtivos.filter(v => idsEmAndamento.includes(v.id));
        break;
      case 'atrasados':
        const idsAtrasados = alugueis
          .filter(a => a.status_aluguel === 'Atrasado')
          .map(a => a.id_veiculo);
        dadosFiltrados = veiculosAtivos.filter(v => idsAtrasados.includes(v.id));
        break;
      case 'lavados':
        dadosFiltrados = veiculosAtivos.filter(v => v.limpeza === 'Lavado');
        break;
      case 'sujos':
        dadosFiltrados = veiculosAtivos.filter(v => v.limpeza === 'Sujo');
        break;
      case 'precisaLimpeza':
        const veiculosComUsoAtivo = new Set(
          alugueis
            .filter(a => ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel))
            .map(a => a.id_veiculo)
        );
        // Sujos E disponíveis (sem aluguel ativo) = precisam limpeza urgente
        dadosFiltrados = veiculosAtivos.filter(v => v.limpeza === 'Sujo' && !veiculosComUsoAtivo.has(v.id));
        break;
      case 'alugados':
        const veiculosAlugados = new Set(
          alugueis
            .filter(a => ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel))
            .map(a => a.id_veiculo)
        );
        dadosFiltrados = veiculosAtivos.filter(v => veiculosAlugados.has(v.id));
        break;
      default:
        dadosFiltrados = veiculosAtivos;
    }

    setModalTitulo(titulo);
    setModalDados(dadosFiltrados);
    setBuscaModal('');
    setModalAberto(true);
  };

  // Dados filtrados pela busca no modal
  const dadosFiltradosBusca = useMemo(() => {
    if (!buscaModal.trim()) return modalDados;
    
    const termo = buscaModal.toLowerCase();
    return modalDados.filter(v => 
      v.placa?.toLowerCase().includes(termo) ||
      v.modelo?.toLowerCase().includes(termo) ||
      v.marca?.toLowerCase().includes(termo) ||
      v.proprietario?.toLowerCase().includes(termo)
    );
  }, [modalDados, buscaModal]);

  // Veículos excluídos (soft delete) — separados da frota ativa
  const veiculosExcluidos = useMemo(() => veiculos.filter(v => v.status === 'Excluído'), [veiculos]);
  const veiculosAtivos = useMemo(() => veiculos.filter(v => v.status !== 'Excluído'), [veiculos]);

  // IDs de veículos com manutenção ativa (bloqueia disponibilidade)
  const veiculosEmManutencaoSet = useMemo(() => new Set(
    manutencoes
      .filter(m => ['Em Andamento', 'Agendada'].includes(m.status))
      .map(m => m.veiculo_id)
  ), [manutencoes]);

  const veiculosExcluidosFiltrados = useMemo(() => {
    if (!buscaExcluidos.trim()) return veiculosExcluidos;
    const termo = buscaExcluidos.toLowerCase();
    return veiculosExcluidos.filter(v =>
      v.placa?.toLowerCase().includes(termo) ||
      v.modelo?.toLowerCase().includes(termo) ||
      v.marca?.toLowerCase().includes(termo)
    );
  }, [veiculosExcluidos, buscaExcluidos]);

  // Análise de veículos - BASEADO NOS ALUGUÉIS ATIVOS (fonte de verdade)
  const stats = useMemo(() => {
    // Status de aluguel que indicam veículo em uso
    const statusEmUso = ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'];
    
    // IDs de veículos com aluguéis ativos (qualquer tipo de uso)
    const veiculosComAluguelAtivo = new Set(
      alugueis
        .filter(a => statusEmUso.includes(a.status_aluguel))
        .map(a => a.id_veiculo)
    );

    // Veículos por status de aluguel
    const reservados = alugueis
      .filter(a => a.status_aluguel === 'Reservado')
      .map(a => a.id_veiculo);
    
    const emAndamento = alugueis
      .filter(a => ['Em Andamento', 'Ativo'].includes(a.status_aluguel))
      .map(a => a.id_veiculo);
    
    const atrasados = alugueis
      .filter(a => a.status_aluguel === 'Atrasado')
      .map(a => a.id_veiculo);

    // DISPONÍVEIS = Veículos ATIVOS sem aluguel ativo E sem manutenção ativa
    const disponiveis = veiculosAtivos.filter(v =>
      !veiculosComAluguelAtivo.has(v.id) && !veiculosEmManutencaoSet.has(v.id)
    );
    const emManutencao = veiculosAtivos.filter(v => veiculosEmManutencaoSet.has(v.id));

    // Limpeza — apenas veículos ativos
    const lavados = veiculosAtivos.filter(v => v.limpeza === 'Lavado');
    const sujos = veiculosAtivos.filter(v => v.limpeza === 'Sujo');
    const precisaLimpeza = veiculosAtivos.filter(v => 
      v.limpeza === 'Sujo' && !veiculosComAluguelAtivo.has(v.id)
    );

    // Total de veículos alugados (somente ativos com uso ativo)
    const alugados = veiculosAtivos.filter(v => veiculosComAluguelAtivo.has(v.id)).length;

    // 📅 NOVO: Aluguéis que vencem nos próximos 3 dias
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const proximosDias = new Date(hoje);
    proximosDias.setDate(proximosDias.getDate() + 3);

    const aluguelsPorVencer = alugueis.filter(a => {
      const dataDevolucao = new Date(a.data_devolucao);
      dataDevolucao.setHours(0, 0, 0, 0);
      return dataDevolucao <= proximosDias && dataDevolucao >= hoje && 
             ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel);
    });

    return {
      total: veiculosAtivos.length,
      excluidos: veiculosExcluidos.length,
      disponiveis: disponiveis.length,
      alugados: alugados,
      reservados: reservados.length,
      emAndamento: emAndamento.length,
      atrasados: atrasados.length,
      lavados: lavados.length,
      sujos: sujos.length,
      precisaLimpeza: precisaLimpeza.length,
      emManutencao: emManutencao.length,
      taxaOcupacao: veiculosAtivos.length > 0
        ? ((alugados / veiculosAtivos.length) * 100).toFixed(1)
        : 0,
      aluguelsPorVencer: aluguelsPorVencer
    };
  }, [veiculosAtivos, veiculosExcluidos, alugueis, veiculosEmManutencaoSet]);

  // Gráficos
  useEffect(() => {
    if (veiculosAtivos.length === 0) return;

    // Gráfico de status
    const ctxStatus = document.getElementById('grafico-status');
    if (ctxStatus) {
      const existente = Chart.getChart(ctxStatus);
      if (existente) existente.destroy();

      new Chart(ctxStatus, {
        type: 'doughnut',
        data: {
          labels: ['Disponíveis', 'Alugados (Em Uso)', 'Reservados', 'Atrasados', 'Em Manutenção'],
          datasets: [{
            data: [
              stats.disponiveis,
              stats.emAndamento,
              stats.reservados,
              stats.atrasados,
              stats.emManutencao
            ],
            backgroundColor: ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9e9e9e']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
            title: {
              display: true,
              text: `Total: ${stats.total} | Disponíveis: ${stats.disponiveis} | Em Uso: ${stats.alugados} | Manutenção: ${stats.emManutencao}`
            }
          }
        }
      });
    }

    // Gráfico de limpeza
    const ctxLimpeza = document.getElementById('grafico-limpeza');
    if (ctxLimpeza) {
      const existente = Chart.getChart(ctxLimpeza);
      if (existente) existente.destroy();

      new Chart(ctxLimpeza, {
        type: 'bar',
        data: {
          labels: ['Lavados', 'Sujos', 'Precisam Limpeza'],
          datasets: [{
            label: 'Veículos',
            data: [stats.lavados, stats.sujos, stats.precisaLimpeza],
            backgroundColor: ['#4caf50', '#ff9800', '#f44336']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true } }
        }
      });
    }
  }, [veiculosAtivos, stats]);

  const handleLimpezaChange = async (id, novoStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/frota/${id}/limpeza`,
        { status_limpeza: novoStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVeiculos(prev =>
        prev.map(v => (v.id === id ? { ...v, limpeza: novoStatus } : v))
      );
      
      mostrarSnackbar('Status de limpeza atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar limpeza:', err);
      mostrarSnackbar('Erro ao atualizar status de limpeza', 'error');
    }
  };

  // Status do veículo: manutenção tem prioridade, depois verifica aluguéis
  const getStatusVeiculo = (veiculo) => {
    if (veiculosEmManutencaoSet.has(veiculo.id)) {
      return { status: 'Em Manutenção', color: 'default', icon: <BuildIcon /> };
    }

    const aluguelAtivo = alugueis.find(
      a => a.id_veiculo === veiculo.id &&
      ['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente', 'Atrasado'].includes(a.status_aluguel)
    );

    if (!aluguelAtivo) {
      return { status: 'Disponível', color: 'success', icon: <AvailableIcon /> };
    }

    if (aluguelAtivo.status_aluguel === 'Reservado') {
      return { status: 'Reservado', color: 'warning', icon: <BusyIcon /> };
    }

    if (aluguelAtivo.status_aluguel === 'Atrasado') {
      return { status: 'Atrasado', color: 'error', icon: <CancelIcon /> };
    }

    return { status: 'Em Uso', color: 'info', icon: <BusyIcon /> };
  };

  // Colunas da tabela
  const colunas = useMemo(() => [
    { 
      accessorKey: 'placa', 
      header: 'Placa',
      size: 100,
      Cell: ({ row }) => (
        <Box
          onClick={() => abrirAluguelsPorVeiculo(row.original)}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' },
            padding: '8px',
            borderRadius: 1,
            '&:hover': { bgcolor: '#f0f0f0' }
          }}
        >
          {row.original.placa}
        </Box>
      )
    },
    { 
      accessorKey: 'modelo', 
      header: 'Modelo',
      size: 150
    },
    { 
      accessorKey: 'marca', 
      header: 'Marca',
      size: 120
    },
    {
      accessorKey: 'status_real',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => {
        const statusInfo = getStatusVeiculo(row.original);
        return (
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.status}
            color={statusInfo.color}
            size="small"
          />
        );
      }
    },
    {
      accessorKey: 'limpeza',
      header: 'Limpeza',
      size: 140,
      Cell: ({ row }) => (
        <FormControl size="small" fullWidth>
          <Select
            value={row.original.limpeza || 'Sujo'}
            onChange={(e) => handleLimpezaChange(row.original.id, e.target.value)}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="Lavado">✓ Lavado</MenuItem>
            <MenuItem value="Sujo">✗ Sujo</MenuItem>
          </Select>
        </FormControl>
      )
    },
    {
      accessorKey: 'preco_dia',
      header: 'Diária',
      size: 100,
      Cell: ({ cell }) => 
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
          .format(cell.getValue() || 0)
    }
  ], [alugueis]);

  // Pré-computa quantos veículos disponíveis para cada duração de 1 a 15 dias
  const disponiveisPorDuracao = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const resultado = {};
    for (let dias = 1; dias <= 15; dias++) {
      const dataFim = new Date(hoje);
      dataFim.setDate(dataFim.getDate() + dias);
      resultado[dias] = veiculosAtivos.filter(v => {
        if (veiculosEmManutencaoSet.has(v.id)) return false;
        return !alugueis.some(a => {
          if (!['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel)) return false;
          const retAluguel = new Date(a.data_retirada);
          const devAluguel = new Date(a.data_devolucao);
          return retAluguel <= dataFim && devAluguel >= hoje;
        });
      }).length;
    }
    return resultado;
  }, [veiculosAtivos, alugueis, veiculosEmManutencaoSet]);

  // Abre modal com veículos disponíveis para um período a partir de hoje
  const abrirModalDuracao = (dias) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const dataFim = new Date(hoje);
    dataFim.setDate(dataFim.getDate() + dias);

    const dadosFiltrados = veiculosAtivos.filter(v => {
      if (veiculosEmManutencaoSet.has(v.id)) return false;
      return !alugueis.some(a => {
        if (!['Reservado', 'Em Andamento', 'Ativo', 'Agendado', 'Pendente'].includes(a.status_aluguel)) return false;
        const retAluguel = new Date(a.data_retirada);
        const devAluguel = new Date(a.data_devolucao);
        return retAluguel <= dataFim && devAluguel >= hoje;
      });
    });

    const label = dias === 1 ? '24 horas' : `${dias} dias`;
    setModalTitulo(`Disponíveis para ${label} (a partir de hoje)`);
    setModalDados(dadosFiltrados);
    setBuscaModal('');
    setModalAberto(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h6">Carregando dados da frota...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ px: 3, mt: 2, mb: 4, width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <CarIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Gestão da Frota
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Controle completo de veículos, status e limpeza
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Button variant="outlined" onClick={carregarDados} disabled={loading}>
            Atualizar Dados
          </Button>
        </Box>
      </Box>

      {/* Alertas */}
      {stats.precisaLimpeza > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            ⚠️ Atenção! {stats.precisaLimpeza} veículo(s) disponível(is) precisa(m) de limpeza urgente!
          </Typography>
        </Alert>
      )}

      {/* 🚨 ALERTAS INTELIGENTES EM TEMPO REAL */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* 📅 Devoluções nos próximos 3 dias */}
        {stats.aluguelsPorVencer.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ border: '2px solid #2196f3' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  📅 {stats.aluguelsPorVencer.length} devolução(ões) nos próximos 3 dias:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {stats.aluguelsPorVencer.map((aluguel) => {
                    const dias = Math.ceil(
                      (new Date(aluguel.data_devolucao) - new Date()) / (1000 * 60 * 60 * 24)
                    );
                    return (
                      <Chip
                        key={aluguel.id}
                        label={`${aluguel.placa} (${dias}d)`}
                        color={dias === 0 ? 'error' : 'warning'}
                        variant="outlined"
                        size="small"
                      />
                    );
                  })}
                </Stack>
              </Box>
            </Alert>
          </Grid>
        )}

        {/* 🚗 Carros parados (disponíveis) */}
        {stats.disponiveis > 0 && (
          <Grid item xs={12}>
            <Alert severity="success" sx={{ border: '2px solid #4caf50' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  🚗 {stats.disponiveis} veículo(s) disponível(is) AGORA para aluguel!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Taxa de ocupação: {stats.taxaOcupacao}% | {stats.alugados}/{stats.total} carros em uso
                </Typography>
              </Box>
            </Alert>
          </Grid>
        )}

        {/* ⏰ Aluguéis atrasados */}
        {stats.atrasados > 0 && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ border: '2px solid #f44336' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                🚨 {stats.atrasados} aluguel(is) ATRASADO(S) - Ação imediata necessária!
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* 🔧 Veículos em manutenção */}
        {stats.emManutencao > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ border: '2px solid #9e9e9e', bgcolor: '#f5f5f5' }}
              action={
                <Button size="small" onClick={() => abrirModal('Veículos em Manutenção', 'emManutencao')}>
                  Ver
                </Button>
              }
            >
              <Typography variant="subtitle2" fontWeight={600}>
                🔧 {stats.emManutencao} veículo(s) em manutenção — indisponíveis para locação
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Cards de Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Total de Veículos', 'total')}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                  <CarIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total de Veículos</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats.total}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos Disponíveis', 'disponiveis')}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                  <AvailableIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Disponíveis</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats.disponiveis}</Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.total > 0 ? ((stats.disponiveis / stats.total) * 100).toFixed(0) : 0}% da frota
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ cursor: 'pointer', bgcolor: '#e3f2fd', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos Alugados', 'alugados')}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                  <BusyIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Alugados (Em Uso)</Typography>
                  <Typography variant="h4" fontWeight={700} color="info.main">{stats.alugados}</Typography>
                  <Typography variant="caption" color="info.main">
                    {stats.taxaOcupacao}% de ocupação
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos com Limpeza', 'lavados')}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ bgcolor: stats.sujos > 0 ? 'error.main' : 'success.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                  <WashIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Limpeza</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {stats.lavados}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">/</Typography>
                    <Typography variant="h5" fontWeight={700} color="error.main">
                      {stats.sujos}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Lavados / Sujos
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cards Detalhados */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ bgcolor: '#fff3e0', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos Reservados', 'reservados')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <BusyIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{stats.reservados}</Typography>
              <Typography variant="body2" color="text.secondary">Reservados</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ bgcolor: '#e3f2fd', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos Em Andamento', 'emAndamento')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SpeedIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{stats.emAndamento}</Typography>
              <Typography variant="body2" color="text.secondary">Em Andamento</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ bgcolor: '#ffebee', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos Atrasados', 'atrasados')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CancelIcon sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{stats.atrasados}</Typography>
              <Typography variant="body2" color="text.secondary">Atrasados</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ bgcolor: '#e8f5e9', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos Lavados', 'lavados')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{stats.lavados}</Typography>
              <Typography variant="body2" color="text.secondary">Veículos Lavados</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ bgcolor: '#fff9c4', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos que Precisam Limpeza', 'precisaLimpeza')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <WashIcon sx={{ fontSize: 40, color: '#fbc02d', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{stats.precisaLimpeza}</Typography>
              <Typography variant="body2" color="text.secondary">Precisam Limpeza</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card sx={{ bgcolor: '#f5f5f5', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => abrirModal('Veículos em Manutenção', 'emManutencao')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <BuildIcon sx={{ fontSize: 40, color: '#9e9e9e', mb: 1 }} />
              <Typography variant="h5" fontWeight={700} color="text.secondary">{stats.emManutencao}</Typography>
              <Typography variant="body2" color="text.secondary">Em Manutenção</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Excluídos */}
        {stats.excluidos > 0 && (
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Card sx={{ bgcolor: '#f5f5f5', height: '100%', cursor: 'pointer', border: '1px dashed #bdbdbd', '&:hover': { boxShadow: 6 } }} onClick={() => setModalExcluidosAberto(true)}>
              <CardContent sx={{ textAlign: 'center' }}>
                <DeleteForeverIcon sx={{ fontSize: 40, color: '#9e9e9e', mb: 1 }} />
                <Typography variant="h5" fontWeight={700} color="text.secondary">{stats.excluidos}</Typography>
                <Typography variant="body2" color="text.secondary">Excluídos</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, minHeight: 380, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <ChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Distribuição da Frota
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 320, position: 'relative' }}>
              <canvas id="grafico-status" style={{ width: '100%' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, minHeight: 380, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <WashIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Status de Limpeza
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 320, position: 'relative' }}>
              <canvas id="grafico-limpeza" style={{ width: '100%' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela de Veículos */}
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Paper sx={{ p: 2, borderRadius: 2, width: '100%' }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Lista Completa da Frota
          </Typography>
          <MaterialReactTable
            columns={colunas}
            data={veiculosAtivos}
            localization={MRT_Localization_PT_BR}
            enablePagination
            enableSorting
            enableColumnFilters
            enableDensityToggle
            initialState={{
              density: 'compact',
              pagination: { pageSize: 25 },
              sorting: [{ id: 'modelo', desc: false }]
            }}
            muiTableContainerProps={{
              sx: { maxHeight: 'none' }
            }}
          />
        </Paper>
      </Box>

      {/* Modal de Detalhes */}
      <Modal
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          width: '95vw',
          height: '95vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden'
        }}>
          {/* Header do Modal */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5'
          }}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="primary">
                {modalTitulo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dadosFiltradosBusca.length} veículo(s) encontrado(s)
              </Typography>
            </Box>
            <IconButton onClick={() => setModalAberto(false)} size="large">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Campo de Busca */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              placeholder="Buscar por placa, modelo, marca ou proprietário..."
              value={buscaModal}
              onChange={(e) => setBuscaModal(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabela com Scroll */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Placa</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Modelo</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Marca</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Proprietário</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Limpeza</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Diária</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dadosFiltradosBusca.length > 0 ? (
                    dadosFiltradosBusca.map((veiculo, index) => {
                      const statusInfo = getStatusVeiculo(veiculo);
                      return (
                        <TableRow key={veiculo.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                          <TableCell>{veiculo.placa}</TableCell>
                          <TableCell>{veiculo.modelo}</TableCell>
                          <TableCell>{veiculo.marca}</TableCell>
                          <TableCell>{veiculo.proprietario}</TableCell>
                          <TableCell>
                            <Chip
                              icon={statusInfo.icon}
                              label={statusInfo.status}
                              color={statusInfo.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={veiculo.limpeza || 'Sujo'}
                              color={veiculo.limpeza === 'Lavado' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                              .format(veiculo.preco_dia || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          Nenhum veículo encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Footer do Modal */}
          <Box sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            bgcolor: '#f5f5f5'
          }}>
            <Button variant="contained" onClick={() => setModalAberto(false)}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Aluguéis do Veículo */}
      <Modal
        open={modalAlugueisAberto}
        onClose={() => setModalAlugueisAberto(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          width: '95vw',
          height: '95vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid #e0e0e0',
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="white">
                📅 Aluguéis - {veiculoSelecionado?.placa} ({veiculoSelecionado?.modelo})
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.8)">
                {aluguelsSelecionados.length} aluguel(is) registrado(s)
              </Typography>
            </Box>
            <IconButton onClick={() => setModalAlugueisAberto(false)} sx={{ color: 'white' }} size="large">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Conteúdo */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
            {loadingAlugueis ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography>Carregando aluguéis...</Typography>
              </Box>
            ) : aluguelsSelecionados.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="h6">Nenhum aluguel encontrado para este veículo</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Retirada</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Devolução</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Pagamento</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aluguelsSelecionados.map((aluguel) => {
                      const situacao = aluguel.situacao_atual;
                      let situacaoCor = 'default';
                      let situacaoIcon = '◯';

                      if (situacao === 'ATRASADO') {
                        situacaoCor = 'error';
                        situacaoIcon = '⚠️';
                      } else if (situacao === 'EM USO') {
                        situacaoCor = 'warning';
                        situacaoIcon = '🚗';
                      } else if (situacao === 'FUTURO') {
                        situacaoCor = 'info';
                        situacaoIcon = '📅';
                      }

                      return (
                        <TableRow key={aluguel.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{aluguel.cliente_nome}</Typography>
                              <Typography variant="caption" color="text.secondary">{aluguel.cliente_cpf}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(aluguel.data_retirada).toLocaleDateString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            {new Date(aluguel.data_devolucao).toLocaleDateString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${situacaoIcon} ${situacao}`}
                              color={situacaoCor}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={aluguel.status_aluguel}
                              color={
                                aluguel.status_aluguel === 'Finalizado' ? 'success' :
                                aluguel.status_aluguel === 'Atrasado' ? 'error' :
                                aluguel.status_aluguel === 'Ativo' ? 'info' :
                                'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={aluguel.status_pagamento === 'Pago' ? '✓ Pago' : '⏳ Pendente'}
                              color={aluguel.status_pagamento === 'Pago' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                              .format(aluguel.valor_diaria || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            bgcolor: '#f5f5f5'
          }}>
            <Button variant="contained" onClick={() => setModalAlugueisAberto(false)}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal Veículos Excluídos */}
      <Modal
        open={modalExcluidosAberto}
        onClose={() => setModalExcluidosAberto(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          width: '90vw',
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.secondary">
                🗑️ Veículos Excluídos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {veiculosExcluidosFiltrados.length} veículo(s) — removidos da frota ativa
              </Typography>
            </Box>
            <IconButton onClick={() => setModalExcluidosAberto(false)} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              placeholder="Buscar por placa, modelo ou marca..."
              value={buscaExcluidos}
              onChange={(e) => setBuscaExcluidos(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Placa</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Modelo</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Marca</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Proprietário</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Diária</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {veiculosExcluidosFiltrados.length > 0 ? (
                    veiculosExcluidosFiltrados.map((v) => (
                      <TableRow key={v.id} sx={{ opacity: 0.7, '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <TableCell>{v.placa}</TableCell>
                        <TableCell>{v.modelo}</TableCell>
                        <TableCell>{v.marca}</TableCell>
                        <TableCell>{v.proprietario || '-'}</TableCell>
                        <TableCell>
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v.preco_dia || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          Nenhum veículo encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', bgcolor: '#f5f5f5' }}>
            <Button variant="contained" onClick={() => setModalExcluidosAberto(false)}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 9999,
            minWidth: 300
          }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      )}
    </Container>
  );
}
